import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/shared/SEOHead';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RotateCcw, Hammer } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAdfBuilder } from '@/hooks/useAdfBuilder';
import { flattenTree } from '@/hooks/useAdfStandard';
import { getTemplateBySlug } from '@/hooks/useAdfTemplates';
import {
  AdfBuilderSidebar,
  AdfBuilderPreview,
  AdfExportDialog,
} from '@/components/adf-standard';

function BuilderSkeleton({ isMobile }: { isMobile: boolean }) {
  return (
    <div className="space-y-6">
      {isMobile ? (
        <div className="border rounded-lg bg-card overflow-hidden">
          <Skeleton className="h-10 w-full" />
          <div className="p-4 space-y-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-5 w-5 rounded" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-0 border rounded-lg bg-card overflow-hidden min-h-[70vh]">
          <div className="border-r p-4 space-y-4">
            <div className="flex items-center justify-between">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-8 w-20" />
            </div>
            <Skeleton className="h-9 w-full" />
            <div className="space-y-2">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="flex items-center gap-3 p-2">
                  <Skeleton className="h-5 w-5 rounded" />
                  <div className="flex-1 space-y-1">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-3 w-40" />
                  </div>
                  <Skeleton className="h-5 w-14 rounded-full" />
                </div>
              ))}
            </div>
          </div>
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <Skeleton className="h-5 w-32" />
              <div className="flex gap-2">
                <Skeleton className="h-8 w-8" />
                <Skeleton className="h-8 w-8" />
              </div>
            </div>
            <Skeleton className="h-10 w-64" />
            <Skeleton className="h-96 w-full rounded-lg" />
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdfStandardBuilder() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState<'elements' | 'preview'>('elements');
  const [exportOpen, setExportOpen] = useState(false);

  const {
    selectedElements,
    isLoading,
    elementTree,
    elementMap,
    toggleElement,
    selectRequired,
    clearSelection,
    generateXml,
    getSelectionStats,
    getSelectedElementsArray,
    applyTemplate,
    templates,
    // Quick Actions
    addTradeInVehicle,
    removeTradeIn,
    addCoBuyer,
    removeCoBuyer,
    tradeInEnabled,
    coBuyerEnabled,
    hasVehicleSelected,
    hasCustomerSelected,
  } = useAdfBuilder();

  const [selectedTemplate, setSelectedTemplate] = useState<string>('');

  // Select all elements
  const selectAll = useCallback(() => {
    if (!elementTree) return;
    const allElements = flattenTree(elementTree);
    allElements.forEach(el => {
      if (!selectedElements.has(el.id)) {
        toggleElement(el.id);
      }
    });
  }, [elementTree, selectedElements, toggleElement]);

  // Handle template selection (database-driven)
  const handleTemplateChange = useCallback((templateSlug: string) => {
    if (templateSlug && templates) {
      const template = getTemplateBySlug(templates, templateSlug);
      if (template) {
        setSelectedTemplate(templateSlug);
        applyTemplate(templateSlug);
        setSearchParams({ template: templateSlug }, { replace: true });
      }
    }
  }, [applyTemplate, templates, setSearchParams]);

  // Handle reset - clear template too
  const handleReset = useCallback(() => {
    clearSelection();
    setSelectedTemplate('');
    setSearchParams({}, { replace: true });
  }, [clearSelection, setSearchParams]);

  // Load from URL params on mount (template or elements)
  useEffect(() => {
    if (!elementTree || elementTree.length === 0 || isLoading) return;

    // Check for template param first
    const templateParam = searchParams.get('template');
    if (templateParam && templates && !selectedTemplate) {
      const template = getTemplateBySlug(templates, templateParam);
      if (template) {
        setSelectedTemplate(templateParam);
        applyTemplate(templateParam);
        return;
      }
    }

    // Otherwise check for elements param
    const elementsParam = searchParams.get('elements');
    if (elementsParam) {
      const slugs = elementsParam.split(',').filter(Boolean);
      const allElements = flattenTree(elementTree);
      
      slugs.forEach(slug => {
        const element = allElements.find(el => el.slug === slug);
        if (element && !selectedElements.has(element.id)) {
          toggleElement(element.id);
        }
      });
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementTree, isLoading, templates]);

  // Update URL when selections change
  useEffect(() => {
    if (selectedElements.size === 0) {
      if (searchParams.has('elements')) {
        setSearchParams({}, { replace: true });
      }
      return;
    }

    const slugs = getSelectedElementsArray().map(el => el.slug);
    const currentParam = searchParams.get('elements');
    const newParam = slugs.join(',');
    
    if (currentParam !== newParam) {
      setSearchParams({ elements: newParam }, { replace: true });
    }
  }, [selectedElements, getSelectedElementsArray, searchParams, setSearchParams]);

  const stats = useMemo(() => getSelectionStats(), [getSelectionStats, selectedElements]);
  const selectedElementsArray = useMemo(() => getSelectedElementsArray(), [getSelectedElementsArray, selectedElements]);

  // Get active template name for display
  const activeTemplateName = useMemo(() => {
    if (!selectedTemplate || !templates) return undefined;
    const template = getTemplateBySlug(templates, selectedTemplate);
    return template?.name;
  }, [selectedTemplate, templates]);

  return (
    <Layout>
      <SEOHead path="/adf-standard/builder" />

      <div className="container py-6">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/adf-standard">ADF Standard</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Builder</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <Hammer className="h-6 w-6 sm:h-7 sm:w-7 text-primary" />
              ADF Builder
            </h1>
            <p className="text-muted-foreground mt-1">
              Create custom ADF subsets for your integration
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={selectedTemplate} onValueChange={handleTemplateChange}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Start from template..." />
              </SelectTrigger>
              <SelectContent>
                {templates?.map((template) => (
                  <SelectItem key={template.slug} value={template.slug}>
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{template.name}</span>
                      {template.description && (
                        <span className="text-xs text-muted-foreground">{template.description}</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedElements.size > 0 && !isLoading && (
              <Button variant="outline" onClick={handleReset} className="gap-2">
                <RotateCcw className="h-4 w-4" />
                Reset
              </Button>
            )}
          </div>
        </div>

        {isLoading ? (
          <BuilderSkeleton isMobile={isMobile} />
        ) : elementTree && elementTree.length > 0 ? (
          isMobile ? (
            // Mobile: Tabbed layout
            <div className="border rounded-lg bg-card overflow-hidden">
              <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'elements' | 'preview')}>
                <TabsList className="w-full rounded-none border-b">
                  <TabsTrigger value="elements" className="flex-1">
                    Elements ({selectedElements.size})
                  </TabsTrigger>
                  <TabsTrigger value="preview" className="flex-1">
                    Preview
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="elements" className="m-0 h-[60vh]">
                  <AdfBuilderSidebar
                    elementTree={elementTree}
                    selectedElements={selectedElements}
                    onToggle={toggleElement}
                    onSelectRequired={selectRequired}
                    onClearSelection={clearSelection}
                    onSelectAll={selectAll}
                    activeTemplate={activeTemplateName}
                    onAddTradeIn={addTradeInVehicle}
                    onRemoveTradeIn={removeTradeIn}
                    onAddCoBuyer={addCoBuyer}
                    onRemoveCoBuyer={removeCoBuyer}
                    tradeInEnabled={tradeInEnabled}
                    coBuyerEnabled={coBuyerEnabled}
                    hasVehicleSelected={hasVehicleSelected}
                    hasCustomerSelected={hasCustomerSelected}
                  />
                </TabsContent>
                <TabsContent value="preview" className="m-0 h-[60vh]">
                  <AdfBuilderPreview
                    selectedCount={selectedElements.size}
                    stats={stats}
                    generateXml={generateXml}
                    onExport={() => setExportOpen(true)}
                  />
                </TabsContent>
              </Tabs>
            </div>
          ) : (
            // Desktop: Side-by-side layout
            <div className="grid grid-cols-2 gap-0 border rounded-lg bg-card overflow-hidden min-h-[70vh]">
              <AdfBuilderSidebar
                elementTree={elementTree}
                selectedElements={selectedElements}
                onToggle={toggleElement}
                onSelectRequired={selectRequired}
                onClearSelection={clearSelection}
                onSelectAll={selectAll}
                activeTemplate={activeTemplateName}
                onAddTradeIn={addTradeInVehicle}
                onRemoveTradeIn={removeTradeIn}
                onAddCoBuyer={addCoBuyer}
                onRemoveCoBuyer={removeCoBuyer}
                tradeInEnabled={tradeInEnabled}
                coBuyerEnabled={coBuyerEnabled}
                hasVehicleSelected={hasVehicleSelected}
                hasCustomerSelected={hasCustomerSelected}
              />
              <AdfBuilderPreview
                selectedCount={selectedElements.size}
                stats={stats}
                generateXml={generateXml}
                onExport={() => setExportOpen(true)}
              />
            </div>
          )
        ) : (
          // Empty state
          <div className="border rounded-lg bg-card p-12 text-center">
            <Hammer className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No ADF Elements Found</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              ADF elements need to be configured before you can use the builder.
            </p>
          </div>
        )}
      </div>

      <AdfExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        selectedElements={selectedElementsArray}
        generateXml={generateXml}
      />
    </Layout>
  );
}
